import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EmailService {
  id: string;
  name: string;
  type: 'notification_api' | 'resend' | 'sendgrid' | 'mailgun' | 'supabase';
  enabled: boolean;
  priority: number;
  config: Record<string, any>;
  status: 'active' | 'inactive' | 'error';
}

export const useEmailWithFailover = () => {
  const [emailServices, setEmailServices] = useState<EmailService[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEmailServices();
  }, []);

  const loadEmailServices = async () => {
    try {
      const { data, error } = await supabase
        .from('email_services')
        .select('*')
        .eq('enabled', true)
        .order('priority', { ascending: true });

      if (error) throw error;
      setEmailServices((data || []).map(service => ({
        id: service.id,
        name: service.name,
        type: service.type as 'notification_api' | 'resend' | 'sendgrid' | 'mailgun' | 'supabase',
        enabled: service.enabled,
        priority: service.priority,
        config: typeof service.config === 'object' ? service.config as Record<string, any> : {},
        status: service.status as 'active' | 'inactive' | 'error'
      })));
    } catch (error) {
      console.error('Error loading email services:', error);
    }
  };

  const sendEmail = async (emailData: {
    to: string;
    subject: string;
    html?: string;
    text?: string;
    type: 'signup' | 'password_reset' | 'notification';
    mergeTags?: Record<string, any>;
  }) => {
    try {
      setLoading(true);
      
      // Use the new NotificationAPI specific function
      const { data, error } = await supabase.functions.invoke('send-notificationapi-email', {
        body: {
          type: emailData.type === 'signup' ? 'signup_confirmation' : emailData.type,
          email: emailData.to,
          parameters: {
            userName: emailData.mergeTags?.userName || 'User',
            confirmationUrl: emailData.mergeTags?.confirmationUrl || '',
            resetUrl: emailData.mergeTags?.resetUrl || ''
          }
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Email sending failed');
      }

      console.log(`Email sent successfully via NotificationAPI`);
      return { success: true, provider: 'NotificationAPI' };
    } catch (error: any) {
      console.error('Email sending error:', error);
      toast.error('Failed to send email');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const sendSignupEmail = async (email: string, confirmationUrl: string, userName?: string) => {
    return await sendEmail({
      to: email,
      subject: 'Welcome! Please confirm your email',
      type: 'signup',
      html: `
        <h1>Welcome${userName ? ` ${userName}` : ''}!</h1>
        <p>Thank you for signing up. Please click the link below to confirm your email:</p>
        <a href="${confirmationUrl}">Confirm Email</a>
      `,
      mergeTags: {
        userName: userName || 'User',
        confirmationUrl
      }
    });
  };

  const sendPasswordResetEmail = async (email: string, resetUrl: string, userName?: string) => {
    return await sendEmail({
      to: email,
      subject: 'Password Reset Request',
      type: 'password_reset',
      html: `
        <h1>Hello${userName ? ` ${userName}` : ''}!</h1>
        <p>You requested a password reset. Click the link below to reset your password:</p>
        <a href="${resetUrl}">Reset Password</a>
        <p>If you didn't request this, please ignore this email.</p>
      `,
      mergeTags: {
        userName: userName || 'User',
        resetUrl
      }
    });
  };

  return {
    emailServices,
    loading,
    sendEmail,
    sendSignupEmail,
    sendPasswordResetEmail,
    loadEmailServices
  };
};